import { getItem, removeItem } from '../../../helpers/store';
import { fields, tnHiddenColumns, deserializeFieldName } from './fields';
import { createFieldsFromColumns } from './tableColumns';

// Pre-created objects to avoid allocations in the loop
const SHOW_TRUE = { show: true };
const SHOW_FALSE = { show: false };

const createSettingAccessor = (fieldSettings) => {
  if (!fieldSettings) return null;

  if (Array.isArray(fieldSettings)) {
    const settingsMap = new Map(
      fieldSettings.map((s) => {
        const key =
          typeof s.fieldId === 'number' ? `custom-field-${s.fieldId}` : deserializeFieldName(s.fieldId) || s.fieldId;
        return [key, { show: s.visible }];
      }),
    );
    return (key) => settingsMap.get(key);
  }

  return fieldSettings instanceof Map ? (key) => fieldSettings.get(key) : (key) => fieldSettings[key];
};

const isShowAllSettings = (fieldSettings) =>
  Array.isArray(fieldSettings) && fieldSettings.length === 1 && fieldSettings[0] === 'all';

const createVisibilityChecker = ({ isPublicListView, fieldSettings }) => {
  const showAll = isShowAllSettings(fieldSettings);
  const hasFieldSettings = isPublicListView && fieldSettings && !showAll;
  const getSettingValue = hasFieldSettings ? createSettingAccessor(fieldSettings) : null;

  return (field, isCustomField = false) => {
    if (field === 'name') return SHOW_TRUE;
    if (showAll) return SHOW_TRUE;
    if (isCustomField) {
      return hasFieldSettings && !getSettingValue(field)?.show ? SHOW_FALSE : SHOW_TRUE;
    }
    if (hasFieldSettings) {
      return getSettingValue(field)?.show ? SHOW_TRUE : SHOW_FALSE;
    }

    return SHOW_TRUE;
  };
};

export const getVisibleFields = (
  customFields = [],
  {
    isPublicListView = false,
    isTalentNetworkEnabled = true,
    fieldSettings = null,
    isListView = false,
    hasRelationshipStrength = false,
  } = {},
) => {
  const result = new Map();
  const safeCustomFields = Array.isArray(customFields) ? customFields : [];
  const getVisibility = createVisibilityChecker({ isPublicListView, fieldSettings });

  fields.forEach((field) => {
    // Exclude fields from the list
    if (field === 'addedBy' && !isListView) return;
    if (!isTalentNetworkEnabled && tnHiddenColumns.has(field)) return;
    // The reachability + connections columns are gated on the relationship_strength flag.
    if ((field === 'reachability' || field === 'connections') && !hasRelationshipStrength) return;

    result.set(field, getVisibility(field));
  });

  if (isPublicListView && isTalentNetworkEnabled) {
    result.set('resume', SHOW_FALSE);
  }

  safeCustomFields.forEach((cf) => result.set(`custom-field-${cf.id}`, getVisibility(`custom-field-${cf.id}`, true)));

  return result;
};

export const isOldColumnsFormat = (storedData) => {
  if (!storedData) return false;
  if (!Array.isArray(storedData)) return false;
  if (storedData.length === 0) return false;

  const firstItem = storedData[0];
  return typeof firstItem === 'object' && firstItem !== null && 'accessor' in firstItem;
};

export const syncMapOrderAndShow = (map1, map2) => {
  const map2Keys = new Set(map2.keys());
  const nextEntries = [];

  Array.from(map2.entries()).forEach(([key, val2]) => {
    if (!map1.has(key)) return;

    const val1 = map1.get(key);

    if (val2?.show === false && val1?.show !== false) {
      nextEntries.push([key, { ...val1, show: false }]);
    } else {
      nextEntries.push([key, val1]);
    }
  });

  Array.from(map1.entries()).forEach(([key, val1]) => {
    if (map2Keys.has(key)) return;
    nextEntries.push([key, val1]);
  });

  map1.clear();
  nextEntries.forEach(([key, val]) => {
    map1.set(key, val);
  });

  return map1;
};

const isMapEntriesFormat = (storedData) => {
  if (!Array.isArray(storedData) || storedData.length === 0) return false;
  const firstItem = storedData[0];
  return Array.isArray(firstItem) && firstItem.length === 2;
};

const CUSTOM_FIELD_PREFIX = 'custom-field-';

// Base columns shipped after a user saved their layout (e.g. reachability +
// connections) get parked at the end by syncMapOrderAndShow. Surface them at
// the front, right after the pinned name column, so existing users actually see
// them; custom fields intentionally keep appending at the end.
const promoteNewBaseColumns = (syncedFields, knownKeys) => {
  const entries = Array.from(syncedFields.entries());
  const isNewBaseColumn = ([key]) => key !== 'name' && !key.startsWith(CUSTOM_FIELD_PREFIX) && !knownKeys.has(key);
  const newColumns = entries.filter(isNewBaseColumn);
  if (newColumns.length === 0) return syncedFields;

  const rest = entries.filter((entry) => !isNewBaseColumn(entry));
  const nameIndex = rest.findIndex(([key]) => key === 'name');
  rest.splice(nameIndex + 1, 0, ...newColumns);

  syncedFields.clear();
  rest.forEach(([key, val]) => syncedFields.set(key, val));
  return syncedFields;
};

const getStoredFields = (storageKey, defaultFields) => {
  const storedFields = getItem(storageKey);
  if (!storedFields) return defaultFields;

  try {
    return JSON.parse(storedFields);
  } catch (error) {
    // Remove invalid/corrupted JSON from storage
    removeItem(storageKey);
    return defaultFields;
  }
};

const manageSavedFields = (isPublicListView, storageKey, visibleFields, fieldSettings) => {
  if (!isPublicListView) return getStoredFields(storageKey, visibleFields);

  if (!fieldSettings || (fieldSettings.length === 1 && fieldSettings[0] === 'all')) {
    return visibleFields;
  }

  return fieldSettings.map((field) => {
    // Normalize field ID to match createSettingAccessor logic
    const normalizedKey =
      typeof field.fieldId === 'number'
        ? `custom-field-${field.fieldId}`
        : deserializeFieldName(field.fieldId) || field.fieldId;
    return [normalizedKey, { show: field.visible }];
  });
};

export const initializeVisibleFields = (
  customFields,
  {
    isPublicListView,
    isTalentNetworkEnabled,
    fieldSettings,
    storageKey,
    isListView = false,
    hasRelationshipStrength = false,
  },
) => {
  const visibleFields = getVisibleFields(customFields, {
    isPublicListView,
    isTalentNetworkEnabled,
    fieldSettings,
    isListView,
    hasRelationshipStrength,
  });
  const storedFields = manageSavedFields(isPublicListView, storageKey, visibleFields, fieldSettings);

  let formatStoredFields;
  if (isOldColumnsFormat(storedFields)) {
    formatStoredFields = createFieldsFromColumns(storedFields);
  } else if (isMapEntriesFormat(storedFields)) {
    formatStoredFields = new Map(storedFields);
  } else {
    formatStoredFields = storedFields;
  }

  // Promotion targets the personal, localStorage-backed layout; public views
  // are driven by server-provided fieldSettings and are left as-is.
  const knownKeys = !isPublicListView && formatStoredFields instanceof Map ? new Set(formatStoredFields.keys()) : null;
  const syncedFields = syncMapOrderAndShow(visibleFields, formatStoredFields);

  if (!knownKeys) return syncedFields;
  return promoteNewBaseColumns(syncedFields, knownKeys);
};
